export { loadEditorSession, saveEditorSession } from '@/lib/project-storage';

import type { EditorSessionParsed } from '@shared/validation';

export interface ResolvedEditorSession {
	openFiles: string[];
	activeFile: string;
	scrollPositions: Map<string, number>;
	cursorPositions: Map<string, { line: number; column: number }>;
}

/**
 * Resolve a persisted session against the set of files that actually exist
 * in the project. Filters out deleted files and stale scroll positions,
 * and falls back `activeFile` to the first surviving tab when necessary.
 *
 * Returns `undefined` when there is nothing useful to restore (no session,
 * empty open files, or all files were deleted).
 */
export function resolveEditorSession(
	session: EditorSessionParsed | undefined,
	existingPaths: Set<string>,
): ResolvedEditorSession | undefined {
	if (!session) return undefined;

	const { openFiles, activeFile, scrollPositions, cursorPositions } = session;
	if (openFiles.length === 0) return undefined;

	// Keep only files that still exist
	const validOpenFiles = openFiles.filter((filePath) => existingPaths.has(filePath));
	if (validOpenFiles.length === 0) return undefined;

	// Keep only scroll positions for surviving files
	const scrollMap = new Map<string, number>();
	for (const [filePath, scrollTop] of Object.entries(scrollPositions)) {
		if (existingPaths.has(filePath) && validOpenFiles.includes(filePath)) {
			scrollMap.set(filePath, scrollTop);
		}
	}

	// Keep only cursor positions for surviving files
	const cursorMap = new Map<string, { line: number; column: number }>();
	for (const [filePath, position] of Object.entries(cursorPositions)) {
		if (existingPaths.has(filePath) && validOpenFiles.includes(filePath)) {
			cursorMap.set(filePath, position);
		}
	}

	const resolvedActiveFile = activeFile && validOpenFiles.includes(activeFile) ? activeFile : validOpenFiles[0];

	return {
		openFiles: validOpenFiles,
		activeFile: resolvedActiveFile,
		scrollPositions: scrollMap,
		cursorPositions: cursorMap,
	};
}
